import { Injectable, Injector, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { RazorpayXAttendanceModel } from '@smarttask-common/src/lib/models/Integrations/RazorpayX/RazorpayXAttendanceModel';
import { Globals } from '@smarttask-common/src/lib/services/globals';
import { DateService } from '@smarttask-common/src/lib/services/General/date.service';
import { CookieService } from 'ngx-cookie-service';
import { OrganizationStoreService } from '../../Organization/store/organizationStore.service';
import { Subject } from 'rxjs';
import { RazorpayXModel } from '@smarttask-common/src/lib/models/Integrations/RazorpayX/RazorpayXModel';
import { RazorpayXEmployeeModel } from '@smarttask-common/src/lib/models/Integrations/RazorpayX/RazorpayXEmployeeModel';
import { DateTime } from 'luxon';

@Injectable({
    providedIn: 'root',
})
export class RazorpayService {
    razorpayx_organization = signal<RazorpayXModel>(undefined);
    razorpayx_employee_id = signal<number>(undefined);
    attendance_data = signal<RazorpayXAttendanceModel>(undefined);

    cookie_name = 'smarttask-razorpay-info';

    constructor(
        private injector: Injector,
        private globals: Globals,
        private http: HttpClient,
        private dateService: DateService,
        private cookieService: CookieService,
        private organizationStoreService: OrganizationStoreService,
    ) {
        this.watchSelectedCompanyId();
    }

    razorpayInit = () => {
        this.fetchRazorpayCompany(this.organizationStoreService.getSelectedOrganizationId()).then((razorpay_company: RazorpayXModel) => {
            //Server returns nothing if the company is not initialized for razorpay
            if (razorpay_company == undefined) {
                return undefined;
            }
            if (razorpay_company && razorpay_company.razorpay_organization_id && razorpay_company.secret_key) {
                this.razorpayx_organization.set(razorpay_company);
            } else {
                this.razorpayx_organization.set(undefined);
                this.attendance_data.set(undefined);
            }

            this.getRazorpayEmployee(this.organizationStoreService.getSelectedOrganizationId()).then((employee_data: RazorpayXEmployeeModel) => {
                const cookie: RazorpayXAttendanceModel = this.cookieService.get(this.cookie_name)
                    ? JSON.parse(this.cookieService.get(this.cookie_name))
                    : undefined;
                if (cookie && employee_data && cookie['employee-id'] === employee_data.razorpay_employee_id) {
                    this.attendance_data.set(cookie);
                }

                this.razorpayx_employee_id.set(employee_data.razorpay_employee_id);
            });
        });
    };

    upsertRazorpayIntegration = (form_data: RazorpayXModel): Promise<any> => {
        return this.http
            .post(`${this.globals.ext_base_url}v1.0/razorpayx/${form_data.organization_id}`, form_data)
            .toPromise()
            .then(
                () => {
                    this.razorpayx_organization.set(form_data);
                },
                (response: HttpErrorResponse) => {
                    return Promise.reject(response);
                },
            );
    };

    fetchRazorpayCompany = (organization_id: number): Promise<RazorpayXModel> => {
        return this.http
            .get(`${this.globals.ext_base_url}v1.0/razorpayx/${organization_id}`)
            .toPromise()
            .then((razorpay_company: RazorpayXModel) => {
                this.razorpayx_organization.set(razorpay_company);
                return razorpay_company;
            });
    };

    upsertRazorpayEmployee = (organization_id: number, razorpay_employee_id: number): Promise<any> => {
        return this.http
            .get(`${this.globals.ext_base_url}v1.0/razorpayx/employee/${organization_id}/${razorpay_employee_id}`)
            .toPromise()
            .then(
                () => {
                    this.razorpayx_employee_id.set(razorpay_employee_id);
                },
                (response: HttpErrorResponse) => {
                    return Promise.reject(response);
                },
            );
    };

    getRazorpayEmployee = (organization_id: number): Promise<RazorpayXEmployeeModel> => {
        return this.http
            .get(`${this.globals.ext_base_url}v1.0/razorpayx/employee/${organization_id}`)
            .toPromise()
            .then((response: RazorpayXEmployeeModel) => {
                this.razorpayx_employee_id.set(response.razorpay_employee_id);
                return response;
            })
            .catch(() => {
                return Promise.resolve(new RazorpayXEmployeeModel());
            });
    };

    checkIn = (): Promise<any> => {
        const current_time = new Date();
        let local_data: RazorpayXAttendanceModel = {
            'employee-id': this.razorpayx_employee_id(),
            date: this.dateService.get_YYYY_MM_DD_format(current_time),
            status: 'present',
            'leave-type': -1,
            checkin: this.dateService.get_HH_mm_format(current_time),
            checkout: undefined,
            remarks: 'Attendance via SmartTask',
        };
        const check_in_model = {
            auth: {
                id: this.razorpayx_organization().razorpay_organization_id,
                key: this.razorpayx_organization().secret_key,
            },
            request: {
                type: 'attendance',
                'sub-type': 'modify',
            },
            data: local_data,
        };

        return this.attendanceInfoUpdate(check_in_model).then((response: any) => {
            if (response) {
                if (response.result.status === 'ok') {
                    this.attendance_data.set(local_data);
                    this.cookieService.set(this.cookie_name, JSON.stringify(local_data), {
                        expires: DateTime.now().plus({ hour: 15 }).toJSDate(),
                        domain: this.globals.cookie_domain,
                    });

                    return response.result.status;
                } else if (response.result.error) return response.result.error.message;
                else return 'Invalid Razorpay Credentials';
            }
        });
    };

    checkOut = (): Promise<any> => {
        const current_time = new Date();
        let local_data: RazorpayXAttendanceModel = {
            'employee-id': this.razorpayx_employee_id(),
            date: this.dateService.get_YYYY_MM_DD_format(current_time),
            status: 'present',
            'leave-type': -1,
            checkin: undefined,
            checkout: this.dateService.get_HH_mm_format(current_time),
            remarks: 'Attendance via SmartTask',
        };
        const check_in_model = {
            auth: {
                id: this.razorpayx_organization().razorpay_organization_id,
                key: this.razorpayx_organization().secret_key,
            },
            request: {
                type: 'attendance',
                'sub-type': 'modify',
            },
            data: local_data,
        };

        return this.attendanceInfoUpdate(check_in_model).then((response: any) => {
            if (response) {
                if (response.result.status === 'ok') {
                    const checkin = this.attendance_data().checkin;
                    this.attendance_data.set(local_data);

                    if (checkin) this.attendance_data().checkin = checkin;

                    this.cookieService.delete(this.cookie_name);

                    return response.result.status;
                } else if (response.result.error) return response.result.error.message;
                else return 'Invalid Razorpay Credentials';
            }
        });
    };

    attendanceInfoUpdate = (model: any) => {
        return this.http
            .post(`${this.globals.ext_base_url}v1.0/razorpayx/checkin/${this.organizationStoreService.getSelectedOrganizationId()}`, model)
            .toPromise();
    };

    watchSelectedCompanyId = () => {
        this.organizationStoreService.watchSelectedOrganizationId().subscribe((selected_organization_id) => {
            if (selected_organization_id != undefined) {
                this.razorpayInit();
            }
        });
    };

    getTooltipText = () => {
        let text = '';
        if (this.attendance_data) {
            if (this.attendance_data().checkin) text += `Check In: ${this.attendance_data().checkin}`;
            if (this.attendance_data().checkout) {
                text += text ? ', ' : '';
                text += `Check Out: ${this.attendance_data().checkout}`;
            }
        } else {
            text = 'Razorpay';
        }

        return text;
    };
}
